import React, { Component } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import moment from 'moment'
import _ from 'lodash'

import { fetchFacilityStats } from '../../../actions/facility/index'
import { withTranslation } from '../../../translations'

import NoTranslate from '../../hocs/NoTranslate'
import Loading from '../../utilities/loading'
import SuccessHubSubtitle from '../../utilities/success_hub_subtitle'
import FacilityStatChart from './chart'

import './facility_dashboard.css'

const trendNames = {
  activeUsersByMonth: {
    yTitleKey: 'dashboard.users',
    nameKey: 'dashboard.activeUsersMonthly',
    buttonKey: 'dashboard.activeUsers',
  },
  newScansByMonth: {
    yTitleKey: 'dashboard.scans',
    nameKey: 'dashboard.newScansMonthly',
    buttonKey: 'dashboard.newScans',
  },
  newUsersByMonth: {
    yTitleKey: 'dashboard.users',
    nameKey: 'dashboard.newUsersMonthly',
    buttonKey: 'dashboard.newUsers',
  },
  coachActivityByMonth: {
    yTitleKey: 'dashboard.coachActivity',
    nameKey: 'dashboard.coachActivityMonthly',
    buttonKey: 'dashboard.coachActivity',
  },
}

const stats = [
  { key: 'users', nameKey: 'dashboard.users', color: '#1CA8DD' },
  { key: 'coaches', nameKey: 'dashboard.coaches', color: '#0da4d7' },
  { key: 'scans', nameKey: 'dashboard.scans', color: '#9F86FF' },
  { key: 'parq', nameKey: 'dashboard.parqs', color: 'rgba(176, 60, 206, 0.9)' },
  { key: 'win', nameKey: 'dashboard.wins', color: '#1BC98E' },
  { key: 'winRate', nameKey: 'dashboard.winRate', color: '#1badc9' },
]

const parseStat = (stat, value, t) => {
  if (stat === 'winRate' && typeof value === 'number') {
    return `${value * 100}%` // winrate only if number is a decimal
  }
  if (typeof value === 'number') {
    return value
  }
  return t('dashboard.notAvailable')
}

const StatCards = ({ facilityStats, t }) => {
  const statCards = stats.map((stat, idx) => {
    const style = {
      color: '#fff',
      borderRadius: '3px',
      backgroundColor: stat.color,
      boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.18)',
    }

    return (
      <div
        key={_.uniqueId()}
        className="col-sm-6 mb-3"
        style={
          idx % 2 === 0 ? { paddingRight: '10px' } : { paddingLeft: '10px' }
        }
      >
        <div className="statcard" style={style}>
          <div className="p-3">
            <span className="statcard-desc" style={{ color: 'white' }}>
              {t(stat.nameKey)}
            </span>
            <h2 className="statcard-number">
              {parseStat(stat.key, facilityStats[stat.key], t)}
            </h2>
          </div>
        </div>
      </div>
    )
  })

  return <div className="row mx-0">{statCards}</div>
}

StatCards.propTypes = {
  facilityStats: PropTypes.object,
  t: PropTypes.func.isRequired,
}

const LeaderBoardList = (coaches, t) =>
  coaches.map(({ name, parq, wins }) => (
    <li key={_.uniqueId()} className="list-group-item">
      <NoTranslate>{name}</NoTranslate>
      <div className="float-right">
        <span>
          {t('dashboard.parq')} <strong>{parq}</strong>
        </span>
        <span className="ml-3">
          {t('dashboard.wins')} <strong>{wins}</strong>
        </span>
      </div>
    </li>
  ))

class FacilityDashboard extends Component {
  constructor(props) {
    super(props)
    this.state = { selectedTrend: 'coachActivityByMonth' }
  }

  componentDidMount() {
    this.props.fetchFacilityStats()

    if (this.props.user.get('coach') || this.props.user.get('facilityAdmin')) {
      if (window._cio && window._cio.page) {
        window._cio.page('/facility/dashboard')
      }
    }
  }

  parseData(trend) {
    return trend.map((trendPoint) => {
      const date = moment(trendPoint.date, 'YYYY-MM')
      const utcDate = Date.UTC(date.get('year'), date.get('month'))
      return [utcDate, trendPoint.value]
    })
  }

  changeTrend(trend, event) {
    event.preventDefault()
    if (this.state.selectedTrend !== trend) {
      this.setState({ selectedTrend: trend })
    }
  }

  // Charts properties
  createSeries(facilityTrend) {
    const { t } = this.props
    const facilityTrends = this.props.facility.get('stats').get('trends').toJS()
    const { nameKey } = trendNames[facilityTrend]

    if (facilityTrend === 'coachActivityByMonth') {
      const { coachActivityByMonth } = facilityTrends
      const parqStatusWithColors = [
        {
          key: 'parqs',
          nameKey: 'dashboard.parqs',
          color: 'rgba(176, 60, 206, 0.9)',
        },
        { key: 'win', nameKey: 'dashboard.win', color: '#31ce99' },
        { key: 'maybe', nameKey: 'dashboard.maybe', color: '#f4c20d' },
        { key: 'no', nameKey: 'dashboard.no', color: '#E64759' },
      ]

      return parqStatusWithColors.map(({ key, nameKey, color }) => {
        const data = []
        coachActivityByMonth.forEach((activity) => {
          data.push({
            date: activity.date,
            value: activity[key],
          })
        })

        return {
          name: t(nameKey),
          data: this.parseData(data),
          color,
        }
      })
    }

    return [
      {
        name: t(nameKey),
        data: this.parseData(facilityTrends[facilityTrend]),
        color: '#66CCCC',
      },
    ]
  }

  renderCharts() {
    const { t } = this.props
    const trends = []
    const facilityTrends = this.props.facility.get('stats').get('trends').toJS()

    for (const facilityTrend in facilityTrends) {
      const { yTitleKey } = trendNames[facilityTrend]
      trends.push({
        code: facilityTrend,
        yTitle: t(yTitleKey),
        series: this.createSeries(facilityTrend),
      })
    }

    // NOTE: pass series through instead of data
    return trends.map(({ code, yTitle, series }) => (
      <div
        key={_.uniqueId()}
        className={this.state.selectedTrend === code ? '' : 'hidden'}
      >
        <FacilityStatChart
          monthTitle={t('dashboard.month')}
          yTitle={yTitle}
          series={series}
        />
      </div>
    ))
  }

  render() {
    const { selectedTrend } = this.state
    const { t } = this.props
    if (!this.props.facility.get('stats')) {
      return <Loading />
    }

    const facilityStats = this.props.facility.get('stats').get('summary').toJS()
    const facilityLeaders = this.props.facility.get('stats').get('leaders')

    return (
      <div className="container">
        <div className="dashhead mb-3">
          <div className="dashhead-titles mt-4">
            <SuccessHubSubtitle className="dashhead-subtitle" />
            <h2 className="dashhead-title">{t('dashboard.usageDashboard')}</h2>
          </div>
        </div>

        {/* <div className='row'>
          <div className='hr-divider mb-4 text-center'>
            <h3
              className='hr-divider-content hr-divider-heading text-center'
              style={ { fontFamily: 'Lato, sans-serif', letterSpacing: '2px' } }>
              Summary
            </h3>
          </div>
        </div> */}

        <div className="p-0 mb-4">
          <div
            className="card-body p-0"
            style={{ boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.18)' }}
          >
            {/* <div className='row mb-4'> */}
            <div className="text-center text-uppercase mb-4 mx-auto facility-dashboard-header">
              {t('dashboard.summary')}
            </div>
            <div className="row px-3 pt-2">
              <div className="statcards col-md-6 pr-0">
                <StatCards facilityStats={facilityStats} t={t} />
              </div>
              <div className="tab-content mb-3 col-md-6">
                <div className="pb-3">
                  {/* <div className={ `text-center text-uppercase mx-auto ${styles.header}` }>
                  Trends
                </div> */}
                  <div className="p-0 text-center mb-3">
                    <div className="btn-group" style={{ width: '90%' }}>
                      <button
                        className={`btn btn-outline-primary ${
                          selectedTrend === 'coachActivityByMonth'
                            ? 'active'
                            : ''
                        }`}
                        style={{
                          width: '25%',
                          fontWeight: 300,
                          borderBottomLeftRadius: '2px',
                          borderTopLeftRadius: '2px',
                        }}
                        onClick={this.changeTrend.bind(
                          this,
                          'coachActivityByMonth'
                        )}
                      >
                        {t(trendNames.coachActivityByMonth.buttonKey)}
                      </button>
                      <button
                        className={`btn btn-outline-primary ${
                          selectedTrend === 'newUsersByMonth' ? 'active' : ''
                        }`}
                        style={{ width: '25%', fontWeight: 300 }}
                        onClick={this.changeTrend.bind(this, 'newUsersByMonth')}
                      >
                        {t(trendNames.newUsersByMonth.buttonKey)}
                      </button>
                      <button
                        className={`btn btn-outline-primary ${
                          selectedTrend === 'activeUsersByMonth' ? 'active' : ''
                        }`}
                        style={{ width: '25%', fontWeight: 300 }}
                        onClick={this.changeTrend.bind(
                          this,
                          'activeUsersByMonth'
                        )}
                      >
                        {t(trendNames.activeUsersByMonth.buttonKey)}
                      </button>
                      <button
                        className={`btn btn-outline-primary ${
                          selectedTrend === 'newScansByMonth' ? 'active' : ''
                        }`}
                        style={{
                          width: '25%',
                          fontWeight: 300,
                          borderBottomRightRadius: '2px',
                          borderTopRightRadius: '2px',
                        }}
                        onClick={this.changeTrend.bind(this, 'newScansByMonth')}
                      >
                        {t(trendNames.newScansByMonth.buttonKey)}
                      </button>
                    </div>
                  </div>
                  <div role="tabpanel" className="tab-pane px-3 mb-3">
                    {this.renderCharts()}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* <div className='row'>
          <div className='hr-divider text-center'>
            <h3
              className='hr-divider-content hr-divider-heading text-center'
              style={ { fontFamily: 'Lato, sans-serif', letterSpacing: '2px' } }>
              Top Users
            </h3>
          </div>
        </div> */}
        <div className="p-0 mb-4">
          <div
            className="card-body p-0"
            style={{ boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.18)' }}
          >
            <div className="text-center text-uppercase mb-3 facility-dashboard-header">
              {t('dashboard.coachActivityLeaders')}
            </div>
            <div className="row p-3">
              <div className="col-md-4">
                <ul className="list-group facility-list-group">
                  <li className="list-group-header">
                    {t('dashboard.coachLeadersCurrentMonth')}
                  </li>
                  {/* <li className='list-group-item'>
                    <span className='mr-3'>John User</span>
                    <span className='icon icon-trophy' />
                    <span className='icon icon-trophy' />
                    <span className='icon icon-trophy' />
                    <div className='float-right'>
                      <span>ParQ <strong>15</strong></span>
                      <span className='ml-3'>Wins <strong>15</strong></span>
                    </div>
                    <span className='list-group-progress' style={ { width: '70%' } } />
                  </li> */}
                  {LeaderBoardList(facilityLeaders.get('last30').toJS(), t)}
                </ul>
              </div>
              <div className="col-md-4">
                <ul className="list-group facility-list-group">
                  <li className="list-group-header">
                    {t('dashboard.coachLeadersAllTime')}
                  </li>
                  {LeaderBoardList(facilityLeaders.get('total').toJS(), t)}
                </ul>
              </div>
              <div className="col-md-4">
                <ul className="list-group facility-list-group">
                  <li className="list-group-header">
                    {t('dashboard.whatIsThis')}
                  </li>
                  <li className="list-group-item">
                    {t('dashboard.coachLeadersExplanation')}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
}

FacilityDashboard.propTypes = {
  fetchFacilityStats: PropTypes.func,
  facility: PropTypes.object,
  t: PropTypes.func.isRequired,
  user: PropTypes.object,
}

const mapStateToProps = (state) => ({
  user: state.user.get('user'),
  facility: state.facility,
})
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ fetchFacilityStats }, dispatch)

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation(FacilityDashboard))
